<template>
  <div class="py-2 my-2 help-container">
    <h1 class="separator py-4">Occupancy WORKSHEET > ACTUAL RENT TAB</h1>
    <span class="banner py-3 mb-4">
      <div>This section will allow you to calculate Actual Rent, which represents the rents actually charged and is used in the Gain/Loss to Lease formula: <strong>Gain/Loss to Lease = Gross Potential Rent - Vacancy Loss - Actual Rent</strong></div>
      <div>There are four types of leases that are addressed on this page to capture Actual Rent:</div>
      <ul class="no-bullet">
        <li class="no-bullet">1. <strong>Active Leases</strong>: These are the current leases. As these leases expire, they will "fall off" of this section. By the end of the year this should have gone to zero.</li>
        <li class="no-bullet">2. <strong>Forecasted Leases</strong>: This is the new renewals and new move ins (#3 and #4) for the rest of the budget year. This number will be zero in your first budget month and will grow each month as leases renew and new residents move in.</li>
        <li class="no-bullet">3. <strong>Renewals</strong>: This is the first month of income that a resident renews</li>
        <li class="no-bullet">4. <strong>Move Ins</strong>: This is the first month of income that a resident moves in</li>
      </ul>
    </span>

    <!--Help Sections-->
    <v-expansion-panels v-model="openSectionInternal">
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(0)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(0)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Top Row Options</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>
            The selection made on the Move Ins/Move Outs numbers regarding entering information at the Property Level or Unit Type level will also be used for Actual Rent. This is because the Actual Rent tab is dependent on numbers from the Move Ins/Move Outs tab.
          </div>
          <div>
            If the Clear & Reset button is clicked, A popup box will appear that reads "This will clear all data from this tab and add back any starting default numbers. Are you sure you want to do this? Cancel or Yes"
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(1)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(1)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Top Summary Section</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>This section is not editable & pulls data from other fields.</div>
          <div><strong>Gross Potential Rent</strong> will display the Gross Potential Rent from the top summary section of this page</div>
          <div><strong>Less Vacancy Loss</strong> will display Vacancy Loss + Vacancy Loss (Down/Admin/Model) from the top summary section of this page</div>
          <div><strong>Less Actual Rent</strong> will display TOTAL ACTUAL RENT from the bottom of this page</div>
          <div><strong>LOSS/(GAIN) TO LEASE</strong> is GPR - Vacancy Loss - Actual Rent</div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(2)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(2)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Settings that affect this page</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>This information is here to help you understand the settings that will affect this page.</div>
          <div>On the settings page, Lease Calculations tab, there will be options that will affect what appears on this page.</div>
          <div><strong>Move In Choices</strong>:</div>
          <ul>
            <li>New Leases will be given a percentage discount off of Market Rent</li>
            <li>New Leases will be at Market Rent</li>
            <li>New Leases will be at Market Rent AND also offer a recurring concession</li>
          </ul>
          <div>This choice will affect what appears on the <strong>New Move-Ins Discount %</strong> line. If you select #1, the percentage discount will appear. If you select either #2 or #3, "MKT" will appear.</div>
          <div><strong>Renewal Choices</strong>:</div>
          <ul>
            <li>Renewals will be given a percentage increase over existing rent</li>
            <li>Renewals will be at Market Rent</li>
            <li>Renewals will be at Market Rent AND also offer a recurring concession</li>
          </ul>
          <div>This choice will affect what appears on the <strong>Renewal Increase %</strong> line. If you select #1, the percentage discount will appear. If you select either #2 or #3, "MKT" will appear.</div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(3)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(3)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Calculations (Property or Unit Type Level)</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>To calculate what rents will be charged in the coming year, the types of leases (and the sections to this tab) have been broken down into four topics:</div>
          <ul>
            <li>Renewal Section - This section addresses the first month that a lease renews. These numbers come directly from the move ins/move outs tab</li>
            <li>New Leases Section - This section addresses the first month that someone moves in. These numbers come directly from the move ins/move outs tab</li>
            <li>Forecasted Lease Section - This section addresses the renewed and new leases after the first month. These numbers will grow throughout the year. It is cumulative, but adjusts when some of the forecasted leases have moved out.</li>
            <li>Contracted Lease section - these are leases as of the beginning of the budget year that are not going to change (they are not renewing, they are not moving out). These numbers will reduce during the year as these leases expire.</li>
          </ul>
          <v-divider></v-divider>
          <div><strong>Average Market Rent</strong> will be Gross Potential Rent displayed above divided by the Total Units displayed in the top summary table.</div>
          <div><strong>Average Contracted Rent</strong> will be the average rent amount for the occupied current leases in that month.</div>
          <div>January (or your first month) numbers can be verified by the steps below. These numbers will be used for the remainder of the year.</div>
          <ul>
            <li>Run an All Units report as of the end of the month prior to the month you are verifying.</li>
            <li>For example, to verify January’s numbers, this date will be 12/31 of the prior year.</li>
            <li>Export the report with a xlsx format and open it in Excel. Click Enable Editing.</li>
            <li>Remove the total rows at the bottom (to allow filtering)</li>
            <li>Add a filter to the headers by</li>
            <li class="no-bullet">
              <ul>
                <li>Click into the title "Lease End Date"</li>
                <li>Select Data > Filter</li>
              </ul>
            </li>
            <li>Click the down arrow in the "Lease End Date" cell</li>
            <li class="no-bullet">
              <ul>
                <li>Select to Sort Oldest to Newest</li>
              </ul>
            </li>
            <li>At the top, delete all rows where the Lease End date ends on or before 12/31</li>
            <li>Scroll to the bottom and remove any units that have no Lease Start date or Lease End date or 0 actual rent.</li>
            <li>At the top, click into Lease Start Date and select to Sort Oldest to Newest.</li>
            <li>Scroll to the bottom and delete all rows where the Lease Start Date is on or after 2/1/22 (there probably won't be any)</li>
            <li>At the top, click into the Lease Status box and if there are any Denied leases, uncheck the box for them.</li>
            <li>Create a formula to get the Average of the Rent column.</li>
            <li>Note: If there happens to be no active lease information from the All Units report, the system will use the Average Market Rent value from the row above</li>
            <li>Note: If you have selected the Unit Type Level view, the system will use averages per unit type</li>
          </ul>
          <v-divider></v-divider>
          <h4><strong>Renewal Section</strong> - This section addresses the first month that a lease renews.</h4>
          <div><strong># Renewals</strong> displays the Projected # Renewed from the MOVE INS / MOVE OUTS tab</div>
          <div><strong>Renewal Increase %</strong> displays the selection entered on the Settings page</div>
          <ul>
            <li>If Choice #2 or #3 was selected, this line will show <strong>MKT</strong> (not editable)</li>
            <li>If Choice #1 was selected, this line will show a <strong>percentage</strong> (editable)</li>
          </ul>
          <div><strong>Average Renewal Increase</strong> applies that selection</div>
          <ul>
            <li>If Choice #2 or #3 was selected, this is Starting Average GPR less Average Existing Rent.</li>
            <li>If Choice #1 was selected, this is Average Existing Rent multiplied by the % increase</li>
          </ul>
          <div><strong>Subtotal Renewal Increase</strong> will display the Average Renewal Increase multiplied by the # Renewals.</div>
          <div>Note: These choices are listed in "Actual Rent - Settings that affect this page" and refer to the selections made on the Lease Calculation tab of Settings.</div>
          <v-divider></v-divider>
          <h4><strong>New Leases Section</strong> - This section addresses the first month that someone moves in.</h4>
          <div><strong># New Leases (Move Ins)</strong> displays the ENDING MOVE INS from the MOVE INS / MOVE OUTS tab</div>
          <div><strong>New Move-In Discount %</strong> displays the selection entered in Settings.</div>
          <ul>
            <li>If Choice #2 or #3 was selected, then this will display <strong>MKT</strong> (not editable)</li>
            <li>If Choice #1 was selected, this will display the <strong>percentage</strong> (editable)</li>
          </ul>
          <div><strong>Total Move-In Discount (only displays if #1 was selected)</strong> displays the resulting discount (if any)</div>
          <ul><li>If Choice #1 was selected, Starting Average GPR multiplied by the percentage in New Move-In Discount %</li></ul>
          <div><strong>Average New Lease Rents (only displays if Choice #1 was selected)</strong></div>
          <ul><li>If Choice #1 was selected, this is Average Market Rent - New Move-In Discount $</li></ul>
          <div><strong>Subtotal New Lease Rent</strong> displays the New Lease Rent after any discounts</div>
          <ul>
            <li>If Choice #2 or #3 was selected, this displays Average Market Rent multiplied by # New Leases (Move-Ins)</li>
            <li>If Choice #1 was selected, this is Average New Lease Rents multiplied by # New Leases (Move-Ins)</li>
          </ul>
          <div>Note: These choices are listed in "Actual Rent - Settings that affect this page" and refer to the selections made on the Lease Calculation tab of Settings.</div>
          <v-divider></v-divider>
          <h4><strong>Forecasted Lease Section</strong> - This section addresses the renewed and new leases after the first month. These numbers will grow throughout the year.</h4>
          <div><strong># Forecasted Leases (Prior Month Renewals & Move Ins)</strong> is the # Renewals (above in the renewal section)+ # New Leases (Move-Ins) (above in the new lease section) from every prior month in the budget year.</div>
          <ul><li>It will always be zero for January and will increase throughout the year</li></ul>
          <div><strong>Subtotal Forecasted Rent</strong> is (Subtotal Renewal Increase) + (Subtotal New Lease Rent) from every prior month in the budget year.</div>
          <div>It will always be zero for January and will increase throughout the year as the number of renewal and new move in count increases.</div>
          <v-divider></v-divider>
          <h4><strong>Contracted Lease Section</strong> - this section addresses existing current leases and what they are paying in rent. As these leases expire, they will drop off.</h4>
          <div><strong># Estimated Contracted Leases (Not renewing or moving out)</strong> is Ending Occupied Units from this month (in the table at the top summary section) - # Renewals - # New Leases (Move Ins) - # Forecasted Leases.</div>
          <div><strong>Subtotal Estimated Contracted Rent</strong> is the total estimated rent amount for the # Estimated Contracted Leases described above. Subtotal Estimated Contracted Rent = Average Occupied Rent * # Estimated Contracted Leases.</div>
          <v-divider></v-divider>
          <div><strong>TOTAL ACTUAL RENT</strong> adds Subtotal Active Leases + Subtotal Forecasted Rent + Subtotal Renewal Increase + Subtotal New Lease Rent.</div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(4)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(4)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Right-Side Icons & Ellipsis Menu Options</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>Within each of the Occupancy tabs you will have additional options on the right-side of the screen.</div>
          <img src="../../assets/help/a.png" />
          <div>
            <img class="small" src="../../assets/help/b.png" />
            <span>This icon will bring up a help screen specific to this page</span>
          </div>
          <div>
            <img class="small" src="../../assets/help/c.png" />
            <span>This icon will open the Comments and Flags screen which will allow you to add a general comment, create a flagged comment, or reply to an existing comment. If it is empty in the middle, there are no comments or flags.</span>
          </div>
          <div>
            <img class="small" src="../../assets/help/d.png" />
            <span>If it has changed to a solid blue, then comments have been entered.</span>
          </div>
          <div>
            <img class="small" src="../../assets/help/e.png" />
            <span>If it has changed to an orange flag, then an unresolved flag or issue exists. Someone will need to click into the Comments and Flags screen and select Resolve the outstanding issue. Once resolved, the icon will change back to blue.</span>
          </div>
          <div>
            <img class="small" src="../../assets/help/f.png" />
            <span>Clicking this ellipsis will bring up a new menu of options.</span>
          </div>
          <br />
          <div><strong>Add Flag or Comment</strong> will bring up the same screen as the center icon allowing you to add a comment, a flagged comment or resolve a comment</div>
          <div><strong>Clear & Reset</strong> will clear out any data that has been added and reset this tab back to the default values when the page was first loaded. This only applies to the specific tab you are on. It will also popup a message to explain what is about to happen and to allow you to confirm or cancel.</div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel readonly>
        <v-expansion-panel-header>
          <v-icon slot="actions"></v-icon>
          <span class="black--text pointer">Prefer to watch a video? <a @click="openExternalTopic('BudgetsPro Occupancy Actual Rent Tab')">Click Here</a></span>
        </v-expansion-panel-header>
      </v-expansion-panel>
      <v-expansion-panel readonly>
        <v-expansion-panel-header>
          <v-icon slot="actions"></v-icon>
          <span class="black--text pointer">Want to see all BudgetsPro help articles? <a @click="openExternalTopic('BudgetsPro')">Click Here</a></span>
        </v-expansion-panel-header>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
  import HelpMixin from '@/mixins/Help.js'
  export default {
    name: 'OccupancyActualRentHelp',
    mixins: [HelpMixin]
  }
</script>

<style lang="scss" scoped>
  .no-bullet {
    list-style-type: none;
  }
  .icon {
      order: 0;
  }
  .header {
      order: 1;
  }
  img {
    max-width: 650px;
  }
  img.small {
    width: 35px;
  }
  .help-container {
      border-bottom: 1px solid var(--v-resGrey-lighten1)
  }
  .help-content div,
  .help-content li {
      margin-top:10px !important;
      margin-bottom:10px !important;
  }
  .help-content h4 {
      margin-top:20px;
  }
  .banner {
    background-color: var(--v-resGrey-lighten1);
    display: block;
  }
  .pointer {
      cursor: pointer !important;
  }
  .separator {
    display: flex;
    align-items: center;
    text-align: center;
  }
  .separator::before,
  .separator::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid var(--v-resGrey-darken1);
  }

  .separator:not(:empty)::before {
    margin-right: .25em;
  }

.separator:not(:empty)::after {
  margin-left: .25em;
}
</style>
